import React, { useState, useRef } from 'react'
import s from './styles.module.scss'
import { Alert, Loader } from 'components'
import * as api from 'api'

export default function UserMediaUpload({ onUploadSuccess }) {
  const [files, setFiles] = useState([])
  const [previews, setPreviews] = useState([])
  const [uploading, setUploading] = useState(false)
  const [caption, setCaption] = useState('')
  const [isPublic, setIsPublic] = useState(true)
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files)
    const validFiles = selectedFiles.filter(file => 
      file.type.startsWith('image/') || file.type.startsWith('video/')
    )

    if (validFiles.length === 0) {
      Alert.warn('Please select image or video files')
      return
    }

    if (validFiles.length + files.length > 10) {
      Alert.warn('Maximum 10 files allowed')
      return
    }

    setFiles(prev => [...prev, ...validFiles])

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader()
      reader.onload = (e) => {
        setPreviews(prev => [...prev, { file, preview: e.target.result, type: file.type.startsWith('video/') ? 'video' : 'image' }])
      }
      reader.readAsDataURL(file)
    })
  }

  const handleRemove = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
    setPreviews(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (files.length === 0) {
      Alert.warn('Please select files to upload')
      return
    }

    setUploading(true)

    try {
      const formData = new FormData()
      files.forEach(file => {
        formData.append('MEDIA', file)
      })
      formData.append('caption', caption)
      formData.append('isPublic', isPublic)

      const response = await api.userAdmin.userMedia.upload(formData)

      if (response.code === 201) {
        Alert.success(`${files.length} file(s) uploaded successfully`)
        setFiles([])
        setPreviews([])
        setCaption('')
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        if (onUploadSuccess) {
          onUploadSuccess()
        }
      } else {
        Alert.error(response.message || 'Failed to upload files')
      }
    } catch (error) {
      console.error('Error uploading files:', error)
      Alert.error('Failed to upload files')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className={s.uploadContainer}>
      <div className={s.uploadHeader}>
        <h3>Upload Photos & Videos</h3>
        <button
          className={s.uploadButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || files.length >= 10}
        >
          <span className="material-icons-outlined">add_photo_alternate</span>
          Select Files
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {previews.length > 0 && (
        <div className={s.previewsGrid}>
          {previews.map((preview, index) => (
            <div key={index} className={s.previewItem}>
              {preview.type === 'video' ? (
                <video src={preview.preview} controls className={s.previewMedia} />
              ) : (
                <img src={preview.preview} alt="Preview" className={s.previewMedia} />
              )}
              <button
                className={s.removeButton}
                onClick={() => handleRemove(index)}
                disabled={uploading}
              >
                <span className="material-icons-outlined">close</span>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={s.uploadOptions}>
        <div className={s.inputGroup}>
          <label>Caption (optional)</label>
          <input
            type="text"
            placeholder="Add a caption..."
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            disabled={uploading}
          />
        </div>

        <div className={s.checkboxGroup}>
          <label>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              disabled={uploading}
            />
            Make public
          </label>
        </div>
      </div>

      {files.length > 0 && (
        <button
          className={s.submitButton}
          onClick={handleUpload}
          disabled={uploading}
        >
          {uploading ? (
            <>
              <Loader color="white" />
              Uploading...
            </>
          ) : (
            `Upload ${files.length} file(s)`
          )}
        </button>
      )}
    </div>
  )
}
